import { IconArrowLeft, IconPlus, IconTrash } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  AdminPageHeader,
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  IconAction,
  NumberInput,
  Select,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useConfirmDialog,
} from "@/components/ui";
import { listarCategoriasDocumento } from "@/features/admin-categorias-documento/services/categorias-documento-service";
import { listarTiposDocumentoAdmin } from "@/features/admin-tipos-documento/services/tipos-documento-service";
import { toastErrorApi, toastExito } from "@/lib/toast";
import { gestionDocumentalApi, type RequisitoDocumental } from "../gestion-documental-service";

const QK = ["admin", "requisitos-documentales"] as const;

/**
 * Gestión Documental (fase 2) · requisitos documentales por (tipo, subtipo) de solicitud: qué
 * categoría —y opcionalmente qué tipo— se exige, cuántos (mínimo/máximo) y si es por CFDI. El paso
 * «Documentos» se arma con ellos y SOLO se puede subir lo configurado (lo opcional = mínimo 0).
 */
export function AdminRequisitosDocumentalesPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirmDialog();

  const { data: tipos = [] } = useQuery({ queryKey: [...QK, "tipos-solicitud"], queryFn: gestionDocumentalApi.tiposDeSolicitud });
  const [tipo, setTipo] = useState("FB60");
  const [subtipo, setSubtipo] = useState("CFDI_UNICO");
  const subtipos = tipos.find((t) => t.codigo === tipo)?.subtipos ?? [];
  useEffect(() => {
    if (subtipos.length && !subtipos.some((s) => s.codigo === subtipo)) setSubtipo(subtipos[0].codigo);
  }, [subtipos, subtipo]);

  const { data: requisitos, isLoading } = useQuery({
    queryKey: [...QK, tipo, subtipo],
    queryFn: () => gestionDocumentalApi.requisitos(tipo, subtipo),
  });
  const invalidar = () => void qc.invalidateQueries({ queryKey: QK });

  const eliminar = useMutation({
    mutationFn: gestionDocumentalApi.eliminarRequisito,
    onSuccess: () => {
      toastExito({ title: "Requisito eliminado", message: "Los documentos ya subidos no cambian." });
      invalidar();
    },
    onError: toastErrorApi,
  });

  const confirmarEliminar = async (r: RequisitoDocumental) => {
    const ok = await confirm({
      title: "Eliminar requisito",
      message: (
        <>
          ¿Eliminar el requisito <b>{r.categoriaNombre}{r.tipoDocumento ? ` · ${r.tipoDocumento}` : ""}</b>? Ya no se podrán subir
          esos documentos en este tipo de solicitud ni contarán para completar el expediente. Los ya subidos no cambian.
        </>
      ),
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(r.id);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-6">
      <AdminPageHeader
        title="Requisitos documentales"
        description="Qué documentos exige cada tipo y subtipo de solicitud · el paso «Documentos» se arma con ellos y solo se puede subir lo configurado."
        actions={
          <Button variant="default" leftSection={<IconArrowLeft size={14} />} onClick={() => void navigate({ to: "/admin" })}>
            Volver
          </Button>
        }
      />
      <Stack gap="md">
        <Card padding="lg">
          <Group gap="md" align="flex-end">
            <Select
              label="Tipo de solicitud"
              options={tipos.map((t) => ({ value: t.codigo, label: t.nombre }))}
              value={tipo}
              onValueChange={setTipo}
              className="w-64"
            />
            <Select
              label="Subtipo"
              options={subtipos.map((s) => ({ value: s.codigo, label: s.nombre }))}
              value={subtipo}
              onValueChange={setSubtipo}
              className="w-72"
            />
          </Group>
        </Card>

        <Alert variant="info" title="Cómo se aplican">
          Un documento cuenta para el requisito que exige su tipo; si ninguno lo exige, para el de su categoría. Los requisitos
          «por CFDI» se piden una vez por cada CFDI (múltiples facturas). La factura que registra el servidor (XML/PDF del CFDI,
          PDF del Invoice) también se exige aquí. Los cambios aplican a las solicitudes que aún no se envían a autorización.
        </Alert>

        <NuevoRequisito tipo={tipo} subtipo={subtipo} onCreado={invalidar} />

        <Card padding="lg">
          <Table verticalSpacing="sm">
            <Thead>
              <Tr>
                <Th className="w-16">Orden</Th>
                <Th>Documento</Th>
                <Th className="w-28">Mínimo</Th>
                <Th className="w-28">Máximo</Th>
                <Th className="w-28">Por CFDI</Th>
                <Th className="w-16" />
              </Tr>
            </Thead>
            <Tbody>
              {(requisitos ?? []).map((r) => (
                <FilaRequisito key={r.id} requisito={r} onCambiado={invalidar} onEliminar={() => void confirmarEliminar(r)} />
              ))}
              {!isLoading && (requisitos ?? []).length === 0 && (
                <Tr>
                  <Td colSpan={6}>
                    <Text size="sm" c="dimmed" ta="center" className="py-3">
                      Sin requisitos · este tipo de solicitud no exige documentos y no permite subir ninguno.
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Card>
      </Stack>
    </div>
  );
}

function FilaRequisito({ requisito, onCambiado, onEliminar }: { requisito: RequisitoDocumental; onCambiado: () => void; onEliminar: () => void }) {
  const [minimo, setMinimo] = useState(requisito.minimo);
  const [maximo, setMaximo] = useState<number | null>(requisito.maximo);
  const [porCfdi, setPorCfdi] = useState(requisito.porCfdi);
  const [orden, setOrden] = useState(requisito.orden);
  const cambiado = minimo !== requisito.minimo || maximo !== requisito.maximo || porCfdi !== requisito.porCfdi || orden !== requisito.orden;

  const guardar = useMutation({
    mutationFn: () => gestionDocumentalApi.editarRequisito(requisito.id, { minimo, maximo, porCfdi, orden }),
    onSuccess: () => {
      toastExito({ title: "Requisito actualizado", message: `${requisito.categoriaNombre}${requisito.tipoDocumento ? ` · ${requisito.tipoDocumento}` : ""}` });
      onCambiado();
    },
    onError: toastErrorApi,
  });

  return (
    <Tr>
      <Td>
        <NumberInput size="xs" value={orden} onChange={(v) => setOrden(typeof v === "number" ? v : 0)} min={0} max={999} />
      </Td>
      <Td>
        <Text size="sm" fw={500}>
          {requisito.categoriaNombre}
        </Text>
        <Group gap={6}>
          {requisito.tipoDocumento ? (
            <Badge size="xs" color="slate">
              {requisito.tipoDocumento}
            </Badge>
          ) : (
            <Text size="xs" c="dimmed">
              Cualquier tipo de la categoría
            </Text>
          )}
        </Group>
      </Td>
      <Td>
        <NumberInput size="xs" value={minimo} onChange={(v) => setMinimo(typeof v === "number" ? v : 0)} min={0} max={100} />
      </Td>
      <Td>
        <NumberInput size="xs" value={maximo ?? undefined} placeholder="Sin tope" onChange={(v) => setMaximo(typeof v === "number" && v > 0 ? v : null)} min={1} max={100} />
      </Td>
      <Td>
        <Checkbox checked={porCfdi} onCheckedChange={(c) => setPorCfdi(c === true)} aria-label="Por CFDI" />
      </Td>
      <Td align="right">
        <Group gap={4} wrap="nowrap" justify="flex-end">
          {cambiado && (
            <Button size="xs" loading={guardar.isPending} onClick={() => guardar.mutate()}>
              Guardar
            </Button>
          )}
          <IconAction label="Eliminar requisito" variant="subtle" iconColor="peligro-600" onClick={onEliminar}>
            <IconTrash size={16} stroke={1.7} />
          </IconAction>
        </Group>
      </Td>
    </Tr>
  );
}

function NuevoRequisito({ tipo, subtipo, onCreado }: { tipo: string; subtipo: string; onCreado: () => void }) {
  const [categoria, setCategoria] = useState<string>("");
  const [tipoDocumento, setTipoDocumento] = useState<string>("");
  const [minimo, setMinimo] = useState(1);
  const [maximo, setMaximo] = useState<number | null>(null);
  const [porCfdi, setPorCfdi] = useState(false);
  const [orden, setOrden] = useState(10);

  const { data: categorias } = useQuery({
    queryKey: ["admin", "categorias-documento", "para-requisitos"],
    queryFn: () => listarCategoriasDocumento({ soloActivas: true, pageSize: 100 }),
  });
  const { data: tipos } = useQuery({
    queryKey: ["admin", "tipos-documento", "para-requisitos", categoria],
    queryFn: () => listarTiposDocumentoAdmin({ categoria, pageSize: 100 }),
    enabled: !!categoria,
  });

  const crear = useMutation({
    mutationFn: () =>
      gestionDocumentalApi.crearRequisito({ tipoSolicitud: tipo, subtipo, categoria, tipoDocumento: tipoDocumento || null, minimo, maximo, porCfdi, orden }),
    onSuccess: (r) => {
      toastExito({ title: "Requisito agregado", message: `${r.categoriaNombre}${r.tipoDocumento ? ` · ${r.tipoDocumento}` : ""}` });
      setCategoria("");
      setTipoDocumento("");
      onCreado();
    },
    onError: toastErrorApi,
  });

  const SIN_TIPO = "__cualquiera__";
  const tiposActivos = (tipos?.items ?? []).filter((t) => t.activo);

  return (
    <Card padding="lg">
      <Stack gap="sm">
        <Text fw={600} size="sm">
          Agregar requisito
        </Text>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6 md:items-end">
          <Select
            label="Categoría"
            options={(categorias?.items ?? []).map((c) => ({ value: c.codigo, label: c.nombre }))}
            value={categoria || undefined}
            onValueChange={(v) => {
              setCategoria(v);
              setTipoDocumento("");
            }}
            placeholder="Selecciona…"
            wrapperClassName="md:col-span-2"
          />
          <Select
            label="Tipo de documento"
            options={[{ value: SIN_TIPO, label: "Cualquier tipo de la categoría" }, ...tiposActivos.map((t) => ({ value: t.nombre, label: t.nombre }))]}
            value={tipoDocumento || SIN_TIPO}
            onValueChange={(v) => setTipoDocumento(v === SIN_TIPO ? "" : v)}
            disabled={!categoria || tiposActivos.length === 0}
            searchable
            wrapperClassName="md:col-span-2"
          />
          <NumberInput label="Mínimo" value={minimo} onChange={(v) => setMinimo(typeof v === "number" ? v : 0)} min={0} max={100} />
          <NumberInput label="Máximo" value={maximo ?? undefined} placeholder="Sin tope" onChange={(v) => setMaximo(typeof v === "number" && v > 0 ? v : null)} min={1} max={100} />
        </div>
        <Group justify="space-between">
          <Group gap="md">
            <Checkbox label="Por cada CFDI (múltiples facturas)" checked={porCfdi} onCheckedChange={(c) => setPorCfdi(c === true)} />
            <NumberInput label="Orden" value={orden} onChange={(v) => setOrden(typeof v === "number" ? v : 0)} min={0} max={999} wrapperClassName="w-28" />
          </Group>
          <Button leftSection={<IconPlus size={14} />} disabled={!categoria || (maximo !== null && maximo < minimo)} loading={crear.isPending} onClick={() => crear.mutate()}>
            Agregar
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}
